import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fillUrl } from "@/lib/formTypes";
import { QuestionEditor } from "@/components/forms/QuestionEditor";
import { FormResponses } from "@/components/forms/FormResponses";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Plus, Save, Link2, ExternalLink, Globe, Lock } from "lucide-react";
import { toast } from "sonner";

const blankQ = () => ({ type: "short_text", label: "", description: "", required: false, options: [] });

function SettingsBar({ form, set }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 grid sm:grid-cols-2 gap-3">
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-1.5">Siapa yang boleh mengisi?</p>
        <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50" data-testid="form-access-toggle">
          {[["internal", "Internal (login)", Lock], ["public", "Publik (link)", Globe]].map(([k, l, I]) => (
            <button key={k} type="button" onClick={() => set("access", k)} data-testid={`form-access-${k}`}
              className={`px-3 py-1.5 text-sm rounded-md font-semibold inline-flex items-center gap-1.5 transition-colors ${form.access === k ? "bg-white shadow text-indigo-700" : "text-slate-500"}`}><I className="h-3.5 w-3.5" />{l}</button>
          ))}
        </div>
      </div>
      <label className="flex items-center justify-between sm:justify-end gap-3 text-sm font-semibold text-slate-700">
        Menerima jawaban<Switch checked={form.is_open} onCheckedChange={(v) => set("is_open", v)} data-testid="form-open-switch" />
      </label>
    </div>
  );
}

export default function FormBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let active = true;
    api.get(`/forms/${id}`).then((r) => { if (active) setForm((f) => f || r.data); })
      .catch(() => { if (active) { toast.error("Form tidak ditemukan"); nav("/form"); } });
    return () => { active = false; };
  }, [id, nav]);
  if (!form) return <div className="py-20 text-center text-slate-400">Memuat...</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setQ = (i, patch) => set("questions", form.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const move = (i, d) => {
    const j = i + d; if (j < 0 || j >= form.questions.length) return;
    const qs = [...form.questions]; [qs[i], qs[j]] = [qs[j], qs[i]]; set("questions", qs);
  };
  const save = async () => {
    setSaving(true);
    try { const r = await api.put(`/forms/${id}`, form); setForm(r.data); toast.success("Form disimpan"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    setSaving(false);
  };
  const copy = () => { navigator.clipboard?.writeText(fillUrl(id)); toast.success("Link form disalin"); };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <button onClick={() => nav("/form")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-forms"><ArrowLeft className="h-4 w-4" />Semua form</button>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={copy} data-testid="copy-form-link-btn"><Link2 className="h-4 w-4 mr-1" />Salin link</Button>
          <Button variant="outline" onClick={() => window.open(fillUrl(id), "_blank")} data-testid="preview-form-btn"><ExternalLink className="h-4 w-4 mr-1" />Pratinjau</Button>
          <Button onClick={save} disabled={saving} data-testid="save-form-btn" className="bg-indigo-600 hover:bg-indigo-700"><Save className="h-4 w-4 mr-1" />{saving ? "Menyimpan..." : "Simpan"}</Button>
        </div>
      </div>
      <Tabs defaultValue="pertanyaan">
        <TabsList className="mb-4">
          <TabsTrigger value="pertanyaan" data-testid="tab-questions">Pertanyaan</TabsTrigger>
          <TabsTrigger value="jawaban" data-testid="tab-responses">Jawaban ({form.response_count})</TabsTrigger>
        </TabsList>
        <TabsContent value="pertanyaan" className="space-y-4 max-w-4xl">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 border-t-8 border-t-indigo-500">
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="text-xl font-extrabold border-0 border-b rounded-none px-0 focus-visible:ring-0" placeholder="Judul form" data-testid="form-title-input" />
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-3" rows={2} placeholder="Deskripsi form" data-testid="form-desc-input" />
          </div>
          <SettingsBar form={form} set={set} />
          {form.questions.map((q, i) => (
            <QuestionEditor key={q.id || i} q={q} idx={i} total={form.questions.length} onChange={(p) => setQ(i, p)} onMove={(d) => move(i, d)}
              onRemove={() => set("questions", form.questions.filter((_, j) => j !== i))}
              onDuplicate={() => set("questions", [...form.questions.slice(0, i + 1), { ...q, id: undefined }, ...form.questions.slice(i + 1)])} />
          ))}
          <Button variant="outline" onClick={() => set("questions", [...form.questions, blankQ()])} className="w-full border-dashed h-12" data-testid="add-question-btn"><Plus className="h-4 w-4 mr-1" />Tambah Pertanyaan</Button>
        </TabsContent>
        <TabsContent value="jawaban"><FormResponses form={form} onCount={(n) => set("response_count", n)} /></TabsContent>
      </Tabs>
    </div>
  );
}
